<template>
  <div>
    <div class="flex items-center space-x-4">
      <h2>{{ isEditing ? "Editar" : "Crear" }} Usuario</h2>
    </div>

    <div
      v-if="!loading"
      class="card mt-2"
    >
      <form @submit.prevent="save">
        <section-header title="Información Personal" />

        <div class="form-grid">
          <div class="form-field">
            <label for="name">Nombre(s)</label>
            <input id="name" type="text" v-model="form.name" />
            <validation-errors :errors="errorFor('name')"></validation-errors>
          </div>

          <div class="form-field">
            <label for="last_name">Apellido(s)</label>
            <input id="last_name" type="text" v-model="form.last_name" />
            <validation-errors
              :errors="errorFor('last_name')"
            ></validation-errors>
          </div>

          <div class="form-field">
            <label for="role">Rol</label>
            <custom-select
              id="role"
              v-model="form.role_id"
              :options="getRoleOptions()"
            />
            <validation-errors :errors="errorFor('role')"></validation-errors>
          </div>

          <div class="form-field">
            <label for="email">Correo electrónico</label>
            <input id="email" type="email" v-model="form.email" />
            <validation-errors :errors="errorFor('email')"></validation-errors>
          </div>
        </div>

        <section-header title="Contraseña" />

        <div class="form-grid">
          <div class="form-field">
            <label for="password">Contraseña</label>
            <input id="password" type="password" v-model="form.password" />
            <validation-errors :errors="errorFor('password')"></validation-errors>
            <password-validation
              class="mt-1"
              :password="form.password"
            ></password-validation>
          </div>

          <div class="form-field">
            <label for="passwordConfirmation">Confirmar contraseña</label>
            <input
              id="passwordConfirmation"
              type="password"
              v-model="form.password_confirmation"
            />
            <password-validation
              class="mt-1"
              :password="form.password"
              :passwordConfirmation="form.password_confirmation"
              :confirmation="true"
            ></password-validation>
            <validation-errors
              :errors="errorFor('password_confirmation')"
            ></validation-errors>
          </div>
        </div>

        <section-header title="Sucursales" />

        <div class="grid grid-cols-1 sm:grid-cols-2">
          <div
            v-for="branch in user.franchise_user.franchise.branches"
            :key="branch.id"
            class="flex items-center mt-2 mr-8"
          >
            <input
              id="terms"
              type="checkbox"
              class="h-5 w-5"
              v-model="form.branchIds[branch.id]"
            />
            <span for="terms" class="ml-2">{{ branch.name }}</span>
          </div>
        </div>
        <validation-errors :errors="errorFor('branches')"></validation-errors>

        <primary-button type="submit" class="mt-10 mx-0" :loading="loadingForm"
          >{{ isEditing ? "Guardar" : "Crear" }}
        </primary-button>
      </form>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import PasswordValidation from "@/components/ui/PasswordValidation";
import api from "@/api";
import validationErrors from "@/mixins/validationErrors";
import { is422 } from "@/utils/response";
import { mapState } from "vuex";
export default {
  name: "Users Form",

  components: {
    PasswordValidation,
  },

  data() {
    return {
      form: {
        name: "",
        last_name: "",
        role_id: null,
        email: "",
        password: "",
        password_confirmation: "",
        branchIds: {},
      },
      loading: false,
      loadingForm: false,
      isEditing: false,
      roles: null,
    };
  },

  computed: {
    ...mapState({
      user: "user",
    }),
  },

  created() {
    this.$store.commit("setSelectedTab", "users");

    this.loadRoles();

    if (this.$route.params.id) {
      this.isEditing = true;
      this.loadUser();
    }
  },

  mixins: [validationErrors],

  methods: {
    async loadRoles() {
      this.loading = true;

      try {
        this.roles = await api.roles.getAll();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    async loadUser() {
      this.loading = true;

      try {
        let franchiseUser = await api.users.getById(this.$route.params.id);
        franchiseUser.email = franchiseUser.user.email;
        franchiseUser.role_id = franchiseUser.user.role.id;
        franchiseUser.password = "";
        franchiseUser.password_confirmation = "";
        franchiseUser.branchIds = {};
        franchiseUser.branches.forEach((branch) => {
          franchiseUser.branchIds[branch.id] = true;
        });

        delete franchiseUser.id;
        delete franchiseUser.user_id;
        delete franchiseUser.user;
        delete franchiseUser.branches;

        this.form = await JSON.parse(JSON.stringify(franchiseUser));
      } catch (error) {
        console.log(error.message);
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    getRoleOptions() {
      const options = [{ value: null, label: "Seleccionar", disabled: false }];

      this.roles.forEach((role) => {
        options.push({
          value: role.id,
          label: role.name,
        });
      });

      return options;
    },

    save() {
      if (!this.validateForm()) {
        this.$store.dispatch(
          "errorSnack",
          "Favor de llenar todos los campos correctamente."
        );
        return;
      }

      if (!this.isEditing) {
        this.createUser();
      } else {
        this.updateUser();
      }
    },

    validateForm() {
      this.errors = {};

      if (!this.form.name) {
        this.errors["name"] = ["Campo requerido"];
      }

      if (!this.form.last_name) {
        this.errors["last_name"] = ["Campo requerido"];
      }

      if (!this.form.role_id) {
        this.errors["role"] = ["Campo requerido"];
      }

      if (!this.form.email) {
        this.errors["email"] = ["Campo requerido"];
      }

      if (
        !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
          this.form.email
        )
      ) {
        this.errors["email"] = ["Correo electrónico inválido."];
      }

      if (this.isEditing) {
        if (
          this.form.password !== "" ||
          this.form.password_confirmation != ""
        ) {
          if (!this.form.password) {
            this.errors["password"] = ["Campo requerido"];
          }

          if (
            !/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*.])(?=.{8,})/.test(
              this.form.password
            )
          ) {
            this.errors["password"] = ["Contraseña inválida."];
          }

          if (!this.form.password_confirmation) {
            this.errors["password_confirmation"] = ["Campo requerido"];
          }
        }
      } else {
        if (!this.form.password) {
          this.errors["password"] = ["Campo requerido"];
        }

        if (
          !/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*.])(?=.{8,})/.test(
            this.form.password
          )
        ) {
          this.errors["password"] = ["Contraseña inválida"];
        }

        if (!this.form.password_confirmation) {
          this.errors["password_confirmation"] = ["Campo requerido"];
        }
      }

      if (this.form.password !== this.form.password_confirmation) {
        this.errors["password_confirmation"] = ["La contraseña no coincide"];
      }

      if (!Object.values(this.form.branchIds).includes(true)) {
        this.errors["branches"] = ["Debes agregar al menos una sucursal"];
      }

      return Object.keys(this.errors).length === 0;
    },

    async createUser() {
      this.loadingForm = true;

      try {
        const response = await api.users.create(this.form);
        this.$store.dispatch("successSnack", response.message);
        this.$router.push({ name: "usersIndex" });
      } catch (error) {
        if (is422(error)) {
          this.errors = error.response && error.response.data.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingForm = false;
    },

    async updateUser() {
      this.loadingForm = true;

      try {
        const response = await api.users.update(
          this.$route.params.id,
          this.form
        );
        this.$store.dispatch("successSnack", response.message);
        this.$router.push({ name: "usersIndex" });
      } catch (error) {
        if (is422(error)) {
          this.errors = error.response && error.response.data.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingForm = false;
    },
  },
};
</script>